import { Either } from 'fp-ts/lib/Either'
import { Location, Route } from 'vue-router'

import { CurrentUser, CurrentUserScheme } from '@/services/api/current-user'
import { log } from '@/util/log'
import { buildAbsoluteSpaPathForRedirect } from '@spa/router/hooks/lib/build-absolute-spa-path-for-redirect'

type RouteLoginRequired = Pick<Route, 'meta' | 'path' | 'query' | 'hash' | 'matched'>

interface RequireLoginDep {
  getCurrentUser: () => Promise<Either<Error, Pick<CurrentUserScheme, 'user_status'>>>
}

type RequireLoginBuilder = (dep: RequireLoginDep)
  => (to: RouteLoginRequired) => Promise<Location | null>

export const buildRequireLogin: RequireLoginBuilder = (dep: RequireLoginDep) => (
  async (to: RouteLoginRequired): Promise<Location | null> => {
    if (!to.matched.some(route => route.meta.requireLogin)) {
      return null
    }

    const currentUserResult = await dep.getCurrentUser()
    const userIsRegistered = currentUserResult.fold(
      (e) => {
        log.error('error on load user_status', e)

        return false
      },
      user => user.user_status === 'registered'
    )

    log.debug('requireLogin hook triggered, current user is', currentUserResult.value)

    if (userIsRegistered) {
      return null
    }

    return {
      path: '/signup',
      // vue-router のフックを経由している = SPA内部の遷移なので
      // spa用のパスに変換している
      query: { next: buildAbsoluteSpaPathForRedirect(to.path), ...to.query },
      hash: to.hash,
    }
  }
)

export const requireLogin = buildRequireLogin({
  /**
   * requireLoginなパスを表示する際に必ずcurrent_userを取得することになる。
   * パフォーマンスの観点ではVuexを利用してSPA内の遷移ではcurrent_userの再取得を行わないようにしたほうが良い。
   * しかし以下の理由から対応はいったん見送っている。
   * @see https://github.com/my-color/front/pull/7457#issuecomment-1652217047
   */
  getCurrentUser: () => new CurrentUser().getCurrentUser(),
})
